import { useCallback } from 'react';

import {
  BlotterTable,
  BlotterTableFilters,
  Button,
  FormControlSizes,
  FormattedMessage,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  createCSVFileName,
  useIntl,
  useMixpanel,
} from '@talos/kyoko';

import type { ExportDataAsExcelParams } from '@talos/kyoko';
import { defineMessages } from 'react-intl';
import { ExportRowsModal, useExportRowsAction } from '../../ExportRowsModal/ExportRowsModal';
import { useBlotterSettings } from '../BlotterSettingsContext';
import { CustomHyperlinkButtons } from '../CustomHyperlinkButtons';
import { ShowOnlySelfFilter } from '../ShowOnlySelfFilter';
import { BlotterActions } from '../styles';
import type { TabbedBlotterPropsWithSelfFilter } from '../types';
import { useOrderHistoryBlotter } from './hooks/useOrderHistoryBlotter';

export type OrderHistoryBlotterProps = TabbedBlotterPropsWithSelfFilter;

const messages = defineMessages({
  exportOrders: {
    defaultMessage: 'Export Orders',
    id: 'Blotters.OrderHistoryBlotter.exportOrders',
  },
});

export function OrderHistoryBlotter({
  renderInBackground: hidden,
  loading,
  maxRows,
  onStartLoading,
  onFinishedLoading,
  suppressShowOnlySelfOrdersAndTrades = false,
}: OrderHistoryBlotterProps) {
  const mixpanel = useMixpanel();
  const { showOnlySelfOrdersAndTrades } = useBlotterSettings();
  const { blotterTable, blotterTableFilterProps, filterBuilderAccordion } = useOrderHistoryBlotter({
    maxRows,
    showOnlySelfOrders: showOnlySelfOrdersAndTrades,
  });
  const handleExportOrders = useCallback(
    (params: ExportDataAsExcelParams) => {
      onStartLoading();
      blotterTable.exportDataAsCSV({
        includeHiddenColumns: true,
        fileName: createCSVFileName({
          name: 'OrderHistory',
        }),
        exportDatesRaw: params.exportDatesRaw,
      });
      onFinishedLoading();
      mixpanel.track(MixpanelEvent.ExportOrders, {
        value: params.exportDatesRaw ? 'UTC' : 'Local',
      });
    },
    [blotterTable, mixpanel, onFinishedLoading, onStartLoading]
  );

  const { modal, onClickExport } = useExportRowsAction(handleExportOrders);
  const intl = useIntl();

  return (
    <>
      {!hidden && (
        <>
          <BlotterTableFilters
            {...filterBuilderAccordion}
            {...blotterTableFilterProps}
            {...blotterTable.blotterTableFiltersProps}
            prefix={
              <BlotterActions>
                <CustomHyperlinkButtons blotter="orderHistory" />
                <Button
                  size={FormControlSizes.Small}
                  loading={loading}
                  onClick={onClickExport}
                  data-testid="export-orders"
                >
                  <FormattedMessage {...messages.exportOrders} />
                </Button>
                {!suppressShowOnlySelfOrdersAndTrades && <ShowOnlySelfFilter type="orders" />}
              </BlotterActions>
            }
          />
        </>
      )}
      {!hidden && (
        <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      )}
      <BlotterTable {...blotterTable} />
      <ExportRowsModal modalProps={modal} title={intl.formatMessage(messages.exportOrders)} />
    </>
  );
}
